import React, { createContext, useContext, useMemo, useState, useEffect } from 'react'
import config from '../config'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  // Storage keys
  const AUTH_CACHE_KEY = 'auth'
  const AUTH_CACHE_STORAGE_KEY = 'auth_storage' // 'session' | 'local'
  const LEGACY_TOKEN_KEY = 'token'

  // Helper to load cached auth (prefers session then local)
  const getInitialAuth = () => {
    try {
      const sessionRaw = sessionStorage.getItem(AUTH_CACHE_KEY)
      if (sessionRaw) {
        const parsed = JSON.parse(sessionRaw)
        if (!parsed.expiresAt || parsed.expiresAt > Date.now()) return { user: parsed.user || null, token: localStorage.getItem(LEGACY_TOKEN_KEY) || null }
      }
    } catch (_) {}
    try {
      const localRaw = localStorage.getItem(AUTH_CACHE_KEY)
      if (localRaw) {
        const parsed = JSON.parse(localRaw)
        if (!parsed.expiresAt || parsed.expiresAt > Date.now()) return { user: parsed.user || null, token: localStorage.getItem(LEGACY_TOKEN_KEY) || null }
      }
    } catch (_) {}
    return { user: null, token: localStorage.getItem(LEGACY_TOKEN_KEY) || null }
  }

  const initial = getInitialAuth()
  const [user, setUser] = useState(initial.user)
  const [token, setToken] = useState(initial.token)

  // Check for existing token on mount
  useEffect(() => {
    if (token) {
      // Verify token is still valid by making a request
      fetch(config.getApiUrl('/auth/me'), {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => res.json())
      .then(data => {
        if (data.user) {
          setUser(data.user)
          // Refresh cache user (respect chosen storage if set)
          try {
            const chosen = localStorage.getItem(AUTH_CACHE_STORAGE_KEY)
            const cache = JSON.stringify({ user: data.user, expiresAt: Date.now() + 7 * 24 * 60 * 60 * 1000 })
            if (chosen === 'session') {
              sessionStorage.setItem(AUTH_CACHE_KEY, cache)
            } else {
              localStorage.setItem(AUTH_CACHE_KEY, cache)
            }
          } catch (_) {}
        } else {
          localStorage.removeItem('token')
          setToken(null)
          // Clear caches
          sessionStorage.removeItem(AUTH_CACHE_KEY)
          localStorage.removeItem(AUTH_CACHE_KEY)
          localStorage.removeItem(AUTH_CACHE_STORAGE_KEY)
        }
      })
      .catch(() => {
        localStorage.removeItem('token')
        setToken(null)
        sessionStorage.removeItem(AUTH_CACHE_KEY)
        localStorage.removeItem(AUTH_CACHE_KEY)
        localStorage.removeItem(AUTH_CACHE_STORAGE_KEY)
      })
    }
  }, [token])

  const login = async (email, password, remember = false, captcha = {}) => {
    try {
      const response = await fetch(config.getApiUrl('/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          captchaId: captcha?.captchaId,
          captchaValue: captcha?.captchaValue,
        })
      })
      
      if (!response.ok) {
        const error = await response.json()
        const friendlyMessages = {
          invalid_credentials: 'Invalid email or password.',
          captcha_required: 'Please complete the captcha challenge.',
          captcha_invalid: 'Captcha did not match.',
          captcha_expired: 'Captcha expired. Please refresh and try again.',
          email_password_required: 'Email and password are required.',
        }
        throw new Error(friendlyMessages[error.error] || error.error || 'Login failed')
      }
      
      const data = await response.json()
      setToken(data.token)
      setUser(data.user)
      // Always store legacy token for compatibility with existing code paths
      localStorage.setItem(LEGACY_TOKEN_KEY, data.token)

      // Cache user for instant hydration next load
      try {
        const cache = JSON.stringify({ user: data.user, expiresAt: Date.now() + 7 * 24 * 60 * 60 * 1000 })
        if (remember) {
          localStorage.setItem(AUTH_CACHE_KEY, cache)
          localStorage.setItem(AUTH_CACHE_STORAGE_KEY, 'local')
          sessionStorage.removeItem(AUTH_CACHE_KEY)
        } else {
          sessionStorage.setItem(AUTH_CACHE_KEY, cache)
          localStorage.setItem(AUTH_CACHE_STORAGE_KEY, 'session')
          localStorage.removeItem(AUTH_CACHE_KEY)
        }
      } catch (_) {}
      return data.user
    } catch (error) {
      throw error
    }
  }

  const register = async (email, password, role, tenantName, remember = false) => {
    try {
      const response = await fetch(config.getApiUrl('/auth/register'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, role, tenantName })
      })
      
      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Registration failed')
      }
      
      const data = await response.json()
      setToken(data.token)
      setUser(data.user)
      localStorage.setItem(LEGACY_TOKEN_KEY, data.token)
      try {
        const cache = JSON.stringify({ user: data.user, expiresAt: Date.now() + 7 * 24 * 60 * 60 * 1000 })
        if (remember) {
          localStorage.setItem(AUTH_CACHE_KEY, cache)
          localStorage.setItem(AUTH_CACHE_STORAGE_KEY, 'local')
          sessionStorage.removeItem(AUTH_CACHE_KEY)
        } else {
          sessionStorage.setItem(AUTH_CACHE_KEY, cache)
          localStorage.setItem(AUTH_CACHE_STORAGE_KEY, 'session')
          localStorage.removeItem(AUTH_CACHE_KEY)
        }
      } catch (_) {}
      return data.user
    } catch (error) {
      throw error
    }
  }

  const logout = () => {
    setUser(null)
    setToken(null)
    localStorage.removeItem(LEGACY_TOKEN_KEY)
    // Clear both caches
    sessionStorage.removeItem(AUTH_CACHE_KEY)
    localStorage.removeItem(AUTH_CACHE_KEY)
    localStorage.removeItem(AUTH_CACHE_STORAGE_KEY)
  }

  const value = useMemo(() => ({ user, token, login, register, logout }), [user, token])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}


